import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Calendar, Briefcase, CreditCard, Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  scheduled: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  completed: "bg-green-500/20 text-green-400 border-green-500/30",
  no_show: "bg-red-500/20 text-red-400 border-red-500/30",
  canceled: "bg-slate-700 text-slate-400 border-slate-600",
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  active: "bg-blue-500/20 text-blue-400 border-blue-500/30"
};

const ClientPortal = () => {
  const { user, token } = useAuth();
  const [portalData, setPortalData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPortalData = async () => {
      try {
        const response = await axios.get(`${API}/client/portal`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPortalData(response.data);
      } catch (error) {
        toast.error("Failed to load portal data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPortalData();
  }, [token]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { client, appointments, engagement, billing } = portalData || {};

  return (
    <div className="space-y-8" data-testid="client-portal">
      {/* Welcome Section */}
      <div className="glass p-8 rounded-2xl">
        <h1 className="font-heading text-3xl font-bold text-white mb-2">
          Welcome, {client?.contact_name || user?.name}!
        </h1>
        <p className="text-slate-400">
          {client?.business_name} • {client?.business_category}
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Appointments */}
        <Card className="bg-navy-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Calendar className="w-5 h-5 text-primary" />
              Upcoming Appointments
            </CardTitle>
          </CardHeader>
          <CardContent>
            {appointments && appointments.length > 0 ? (
              <div className="space-y-4">
                {appointments.map((apt, index) => (
                  <div
                    key={apt.id || index}
                    className="p-4 rounded-lg bg-navy-950/50 border border-slate-800"
                    data-testid={`client-appointment-${index}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{apt.date}</span>
                      <Badge className={statusColors[apt.status]}>
                        {apt.status}
                      </Badge>
                    </div>
                    <p className="text-slate-400 text-sm">
                      {apt.time} • {apt.duration} minutes
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">
                No upcoming appointments
              </p>
            )}
          </CardContent>
        </Card>

        {/* Engagement Status */}
        <Card className="bg-navy-900/50 border-slate-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Briefcase className="w-5 h-5 text-primary" />
              Engagement Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {engagement ? (
              <div className="p-4 rounded-lg bg-navy-950/50 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white font-medium">{engagement.name}</span>
                  <Badge className={statusColors[engagement.status]}>
                    {engagement.status}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-slate-400 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Engagement started {new Date(engagement.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">
                No active engagement
              </p>
            )}
          </CardContent>
        </Card>

        {/* Billing */}
        <Card className="bg-navy-900/50 border-slate-800 lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <CreditCard className="w-5 h-5 text-primary" />
              Billing Confirmation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billing ? (
              <div className="p-4 rounded-lg bg-navy-950/50 border border-slate-800">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <p className="text-white font-medium mb-1">
                      Discovery & Viability Assessment
                    </p>
                    <p className="text-slate-400 text-sm">
                      Paid on {new Date(billing.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-heading font-bold text-white">
                      ${billing.amount.toLocaleString()}
                    </p>
                    <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                      {billing.status}
                    </Badge>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-800">
                  <p className="text-xs text-slate-500">
                    Reference: {billing.stripe_session_id}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-4">
                No billing records
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientPortal;
